/**
 * Web fonts for an SVG that is exported as an image.
 *
 * The Share button draws a celebration card's SVG into a canvas
 * (utils/image-export.ts). An SVG loaded as an image is sandboxed: it cannot
 * use the page's web fonts, so the exported PNG fell back to the system font
 * even with Inter and Space Grotesk loaded on the page. This module embeds
 * the latin faces the course card uses as data URLs in a <style> inside the
 * SVG being exported.
 *
 * The faces come from the same Google Fonts stylesheet index.html loads. If
 * it cannot be reached (offline, a slow network) the export goes out in the
 * system font: embedFontsInSvg reports false and changes nothing, and the
 * caller shares anyway.
 */

/** The faces the course card draws with: Inter 400-700, Space Grotesk 700. */
export const CARD_FONTS_CSS_URL =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@700&display=swap';
const FETCH_TIMEOUT_MS = 5000;
const SVG_NS = 'http://www.w3.org/2000/svg';

type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

export interface FontFaceSource {
  /** The whole @font-face rule. */
  block: string;
  /** Its font file. */
  url: string;
}

/** The @font-face rules of a Google Fonts stylesheet that cover basic latin. */
export function latinFontFaces(css: string): FontFaceSource[] {
  const faces: FontFaceSource[] = [];
  for (const match of css.matchAll(/@font-face\s*\{([^}]*)\}/g)) {
    const body = match[1];
    const range = /unicode-range:\s*([^;]+)/i.exec(body)?.[1] ?? '';
    if (!/U\+0000-00FF/i.test(range)) continue;
    const url = /url\(\s*['"]?([^'")]+)['"]?\s*\)/.exec(body)?.[1];
    if (!url) continue;
    faces.push({ block: `@font-face {${body}}`, url });
  }
  return faces;
}

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/** One request, its body included, answering to `signal`. */
async function fetchBody<T>(
  fetchImpl: FetchLike,
  url: string,
  signal: AbortSignal,
  read: (response: Response) => Promise<T>,
): Promise<T> {
  const response = await fetchImpl(url, { signal });
  if (!response.ok) throw new Error(`${url} answered ${response.status}`);
  return read(response);
}

async function downloadEmbeddedCss(fetchImpl: FetchLike, signal: AbortSignal): Promise<string> {
  const css = await fetchBody(fetchImpl, CARD_FONTS_CSS_URL, signal, (r) => r.text());
  const faces = latinFontFaces(css);
  if (faces.length === 0) throw new Error('no latin faces in the font stylesheet');
  const files = new Map<string, Promise<string>>();
  for (const { url } of faces) {
    if (!files.has(url)) {
      files.set(
        url,
        fetchBody(fetchImpl, url, signal, (r) => r.arrayBuffer()).then(
          (b) => `data:font/woff2;base64,${toBase64(b)}`,
        ),
      );
    }
  }
  const rules = await Promise.all(
    faces.map(async ({ block, url }) => {
      const dataUrl = await (files.get(url) as Promise<string>);
      return block.replace(/src:[^;]*;/, `src: url(${dataUrl}) format('woff2');`);
    }),
  );
  return rules.join('\n');
}

/**
 * The whole download (the stylesheet, every font file, and their BODIES)
 * under ONE deadline: a single timer aborts the requests and settles the
 * wait. A timer cleared when the headers arrived left a stalled body
 * unbounded, and the Share spinner with it (#1078 review).
 */
async function buildEmbeddedCss(fetchImpl: FetchLike): Promise<string | null> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`the card fonts took longer than ${FETCH_TIMEOUT_MS} ms`));
    }, FETCH_TIMEOUT_MS);
  });
  try {
    return await Promise.race([downloadEmbeddedCss(fetchImpl, controller.signal), deadline]);
  } catch (error) {
    console.warn('[svg-fonts] Could not embed the card fonts; sharing in the system font', error);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// One download per session once it succeeds; a failure is retried next time.
let embeddedCss: Promise<string | null> | null = null;

/** Test seam: forget the downloaded fonts. */
export function resetEmbeddedFontsCache(): void {
  embeddedCss = null;
}

/**
 * Embed the card fonts into `svg` (the copy about to be exported). Returns
 * whether it did; false leaves the SVG untouched.
 */
export async function embedFontsInSvg(
  svg: SVGElement,
  fetchImpl: FetchLike = (url, init) => fetch(url, init),
): Promise<boolean> {
  if (!embeddedCss) {
    embeddedCss = buildEmbeddedCss(fetchImpl).then((css) => {
      if (css === null) embeddedCss = null;
      return css;
    });
  }
  const css = await embeddedCss;
  if (!css) return false;
  const style = document.createElementNS(SVG_NS, 'style');
  style.textContent = css;
  svg.insertBefore(style, svg.firstChild);
  return true;
}
